// App icon badge (Badging API), page side only: the number of cards waiting today.
// Supported by installed web apps on iOS/iPadOS 16.4+ (shown only once notifications are allowed)
// and by Chromium on desktop (Windows taskbar, macOS dock). Elsewhere every call is a no-op.
// The service worker can set the same badge on push (self.navigator.setAppBadge in push-sw.js);
// this helper never touches the SW. Callers: shell/appBadge.ts (when the app is open, after each
// sync and after each session).

type BadgeNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

const nav = (): BadgeNavigator | null => (typeof navigator === 'undefined' ? null : (navigator as BadgeNavigator));

export function badgeSupported(): boolean {
  const n = nav();
  return !!n && typeof n.setAppBadge === 'function';
}

/**
 * Show `count` on the app icon; 0 (or less) clears it. Always sent (no de-duplication): the
 * service worker may have changed the badge since the page last set it.
 */
export async function setAppBadgeCount(count: number): Promise<void> {
  const n = nav();
  if (!n || typeof n.setAppBadge !== 'function') return;
  const c = Math.max(0, Math.floor(count));
  try {
    if (c > 0) await n.setAppBadge(c);
    else if (typeof n.clearAppBadge === 'function') await n.clearAppBadge();
    else await n.setAppBadge(0);
  } catch {
    // Not installed, permission not granted (iOS), or the document is not focused: ignore.
  }
}

export function clearAppBadge(): Promise<void> {
  return setAppBadgeCount(0);
}
